// ABOUTME: Displays family goals grid with add/recalculate functionality
// ABOUTME: Shows goal title, description, and active status with empty state

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { FamilyDetailData } from './types';

interface FamilyGoalsSectionProps {
  family: FamilyDetailData;
  recalculationLoading: boolean;
  onAddGoal: () => void;
  onRecalculateGoals: () => void;
}

const FamilyGoalsSection: React.FC<FamilyGoalsSectionProps> = ({
  family,
  recalculationLoading,
  onAddGoal,
  onRecalculateGoals
}) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Family Goals
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddGoal}
            >
              Add Goal
            </Button>
            <Button
              variant="contained"
              onClick={onRecalculateGoals}
              disabled={recalculationLoading}
              startIcon={recalculationLoading ? <CircularProgress size={20} /> : undefined}
            >
              {recalculationLoading ? 'Reprocessing...' : 'Reprocess Family'}
            </Button>
          </Box>
        </Box>

        {family.goals.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {family.goals.map((goal) => (
              <Card key={goal.id} variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {goal.title}
                    </Typography>
                    <Chip
                      label={goal.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={goal.isActive ? 'success' : 'default'}
                    />
                  </Box>
                  {goal.description && (
                    <Typography variant="body2" color="text.secondary">
                      {goal.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Card variant="outlined" sx={{ textAlign: 'center', py: 4 }}>
            <CardContent>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                This family hasn&apos;t created any goals yet. Goals help motivate children and track progress. Create the first goal to get started!
              </Typography>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default FamilyGoalsSection;
